import React from "react";
import { Button, Input, Label, Tabs, TabsList, TabsTrigger, TabsContent } from "lift-ui";
import { Plus, Trash2, Link2, CloudUpload } from "lucide-react";
import StationCardPreview from "./StationCardPreview";
import { Toastify } from "lift-ui";

export interface StarterItem {
  id: string;
  text: string;
}

export interface StationFormData {
  name: string;
  system_prompt: string;
  default_model: string;
  allowed_tools: string[];
  max_tokens: number;
  temperature: number;
  is_active: boolean;
  policies: Record<string, any>;
  meta_config: Record<string, any>;
  version_note: Record<string, any>;
  parent_buddy_id?: string;
  avatarFile?: File;
}

interface StationFormProps {
  formData: StationFormData;
  onChange: (field: keyof StationFormData, value: any) => void;
  disabled?: boolean;
  disableName?: boolean;
  showValidation?: boolean;
  onPreviewDropdownOpenChange?: (open: boolean) => void;
}

const StationForm: React.FC<StationFormProps> = ({
  formData,
  onChange,
  disabled = false,
  disableName = false,
  showValidation = false,
  onPreviewDropdownOpenChange,
}) => {
  const [avatarInputMode, setAvatarInputMode] = React.useState<"url" | "upload">("url");
  const [avatarPreview, setAvatarPreview] = React.useState<string>("");
  const [isDragOver, setIsDragOver] = React.useState(false);

  const isNameValid = formData.name && formData.name.trim().length > 0;
  const hasValidStarter = formData.meta_config?.base_prompts?.some(
    (starter: StarterItem) => starter.text && starter.text.trim().length > 0
  );
  const isStartersValid = hasValidStarter || formData.system_prompt;

  const startersToastShownRef = React.useRef(false);
  React.useEffect(() => {
    // Show a toast when validation is triggered and starters are invalid.
    // Guarded to avoid spamming toasts on rerenders.
    if (!showValidation) {
      startersToastShownRef.current = false;
      return;
    }

    if (isStartersValid) {
      startersToastShownRef.current = false;
      return;
    }

    if (!startersToastShownRef.current) {
      startersToastShownRef.current = true;
      Toastify("error", "Please add at least one starter question");
    }
  }, [showValidation, isStartersValid]);

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processAvatarFile(file);
  };

  const processAvatarFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    onChange("avatarFile", file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (disabled) return;
    const imageFile = Array.from(e.dataTransfer.files).find((f) => f.type.startsWith("image/"));
    if (imageFile) processAvatarFile(imageFile);
    else alert("Please drop a valid image file");
  };

  const handleClearAvatarFile = () => {
    setAvatarPreview("");
    onChange("avatarFile", undefined);
  };

  const getPreviewData = () => {
    const starters = (formData.meta_config?.base_prompts || [])
      .map((s: StarterItem) => s.text)
      .filter((t: string) => t.trim());
    const avatarUrl =
      avatarPreview || formData.meta_config?.avatar || formData.meta_config?.iconSrc || "";
    return {
      id: "preview",
      name: formData.name || "Station Name",
      icon: avatarUrl,
      buddyType: formData.meta_config?.category || "Category",
      description: formData.meta_config?.description_brief || "Station description will appear here...",
      badgeColor: formData.meta_config?.badgeColor || "bg-blue-100",
      iconBgColor: formData.meta_config?.iconBgColor || "bg-gray-100",
      textColor: formData.meta_config?.textColor || "text-gray-900",
      starters: starters.length > 0 ? starters : [],
      customColor: formData.meta_config?.color || "hsl(var(--primary))",
      customGptUrl: formData.meta_config?.custom_gpt || "",
    };
  };

  const previewData = getPreviewData();

  return (
    <div className="space-y-6">
      {/* Live Preview - top center */}
      <div className="flex justify-center">
        <div className="w-full max-w-sm p-4 rounded-lg border border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">Live Preview</h3>
          <StationCardPreview {...previewData} onDropdownOpenChange={onPreviewDropdownOpenChange} />
        </div>
      </div>

      {/* Form fields */}
      <div className="space-y-5">
        {/* Row 1: Name + Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="station-name">Station Name *</Label>
            <Input
              id="station-name"
              value={formData.name}
              onChange={(e) => onChange("name", e.target.value)}
              disabled={disabled || disableName}
              placeholder="Station name"
              className={showValidation && !isNameValid ? "border-red-500" : ""}
            />
            {showValidation && !isNameValid && (
              <p className="text-sm text-red-600">Station name is required</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={formData.meta_config?.category || ""}
              onChange={(e) =>
                onChange("meta_config", { ...formData.meta_config, category: e.target.value })
              }
              disabled={disabled}
              placeholder="e.g. Content Writing, Data Analysis"
            />
          </div>
        </div>

        {/* Row 2: Description (full width) */}
        <div className="space-y-1.5">
          <Label htmlFor="description">Description</Label>
          <textarea
            id="description"
            value={formData.meta_config?.description_brief || ""}
            onChange={(e) =>
              onChange("meta_config", { ...formData.meta_config, description_brief: e.target.value })
            }
            disabled={disabled}
            placeholder="Brief description..."
            rows={2}
            className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Row 3: Custom GPT + Badge Color */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="custom-gpt">Custom GPT Link</Label>
            <Input
              id="custom-gpt"
              value={formData.meta_config?.custom_gpt || ""}
              onChange={(e) =>
                onChange("meta_config", { ...formData.meta_config, custom_gpt: e.target.value })
              }
              disabled={disabled}
              placeholder="https://chatgpt.com/g/your-custom-gpt"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Badge Color</Label>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-input">
                <input
                  type="color"
                  value={formData.meta_config?.color || "#0f172a"}
                  onChange={(e) =>
                    onChange("meta_config", { ...formData.meta_config, color: e.target.value })
                  }
                  disabled={disabled}
                  className="h-full w-full cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none"
                  style={{
                    backgroundColor:
                      formData.meta_config?.color || "hsl(var(--primary))",
                  }}
                />
              </div>
              <Input
                value={formData.meta_config?.color || ""}
                onChange={(e) =>
                  onChange("meta_config", { ...formData.meta_config, color: e.target.value })
                }
                disabled={disabled}
                placeholder="Hex (optional)"
                className="flex-1 h-9"
              />
            </div>
          </div>
        </div>

        {/* Avatar Image */}
        <div className="space-y-1.5">
          <Label>Avatar Image</Label>
          <Tabs
            value={avatarInputMode}
            onValueChange={(v) => {
              setAvatarInputMode(v as "url" | "upload");
              if (v === "url") handleClearAvatarFile();
            }}
          >
            <TabsList className="grid w-full max-w-[240px] grid-cols-2 gap-0">
              <TabsTrigger value="url" className="gap-1.5">
                <Link2 className="h-3.5 w-3.5" />
                URL
              </TabsTrigger>
              <TabsTrigger value="upload" className="gap-1.5">
                <CloudUpload className="h-3.5 w-3.5" />
                Upload
              </TabsTrigger>
            </TabsList>
            <TabsContent value="url" className="mt-3">
              <Input
                value={formData.meta_config?.avatar || ""}
                onChange={(e) => {
                  onChange("meta_config", { ...formData.meta_config, avatar: e.target.value });
                  handleClearAvatarFile();
                }}
                disabled={disabled}
                placeholder="https://example.com/avatar.jpg"
                className="h-9"
              />
              {formData.meta_config?.avatar && (
                <div className="mt-3 flex items-center gap-3 p-3 rounded-md border border-gray-200 bg-gray-50">
                  <img
                    src={formData.meta_config.avatar}
                    alt="URL preview"
                    className="w-10 h-10 object-cover rounded-md border border-gray-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-600">Preview</p>
                    <p className="text-xs text-gray-500 truncate">
                      {formData.meta_config.avatar.length > 30
                        ? `${formData.meta_config.avatar.substring(0, 30)}...`
                        : formData.meta_config.avatar}
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
            <TabsContent value="upload" className="mt-3">
              <label
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`block w-full rounded-lg border-2 border-dashed cursor-pointer transition-all ${
                  isDragOver
                    ? "border-primary bg-primary/5"
                    : "border-gray-300 bg-transparent hover:border-gray-400 hover:bg-gray-50"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleAvatarFileChange}
                  disabled={disabled}
                />
                <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
                  <CloudUpload className="h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-sm font-semibold mb-1">Drop image here or click to select</p>
                  <p className="text-xs text-gray-500">JPG, PNG, GIF, WebP &middot; Max 5MB</p>
                </div>
              </label>
              {(avatarPreview || formData.avatarFile) && (
                <div className="mt-3 flex items-center gap-3 p-3 rounded-md border border-gray-200 bg-gray-50">
                  {avatarPreview && (
                    <img
                      src={avatarPreview}
                      alt="Upload preview"
                      className="w-10 h-10 object-cover rounded-md border border-gray-200"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {formData.avatarFile?.name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {formData.avatarFile
                        ? `${(formData.avatarFile.size / 1024).toFixed(1)} KB`
                        : ""}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAvatarFile}
                    disabled={disabled}
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Starters */}
        <div className="space-y-2">
          <h3
            className={`text-sm font-semibold ${
              showValidation && !isStartersValid ? "text-red-600" : "text-gray-900"
            }`}
          >
            Starters (Quick Questions) *
          </h3>
          <p
            className={`text-sm ${
              showValidation && !isStartersValid ? "text-red-600" : "text-gray-600"
            }`}
          >
            At least one starter is required
          </p>
          <div className="space-y-2">
            {(formData.meta_config?.base_prompts || []).map((starter: StarterItem, index: number) => (
              <div
                key={starter.id || index}
                className="flex items-center gap-2 p-3 rounded-md border border-gray-200 bg-white"
              >
                <div className="flex-1 min-w-0">
                  <Input
                    value={starter.text}
                    onChange={(e) => {
                      const updated = [...(formData.meta_config?.base_prompts || [])];
                      updated[index] = { ...starter, text: e.target.value };
                      onChange("meta_config", { ...formData.meta_config, base_prompts: updated });
                    }}
                    disabled={disabled}
                    placeholder="Build a brief in guided mode."
                    className="h-9"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const updated = [...(formData.meta_config?.base_prompts || [])];
                    updated.splice(index, 1);
                    onChange("meta_config", { ...formData.meta_config, base_prompts: updated });
                  }}
                  disabled={disabled || (formData.meta_config?.base_prompts || []).length <= 1}
                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newStarter: StarterItem = { id: `starter-${Date.now()}`, text: "" };
                const updated = [...(formData.meta_config?.base_prompts || []), newStarter];
                onChange("meta_config", { ...formData.meta_config, base_prompts: updated });
              }}
              disabled={disabled}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add Starter
            </Button>
          </div>
        </div>

        {/* Version note */}
        {formData.parent_buddy_id && (
          <div className="space-y-1.5">
            <Label htmlFor="version-note">Version Note</Label>
            <textarea
              id="version-note"
              value={formData.version_note?.description || ""}
              onChange={(e) =>
                onChange("version_note", { ...formData.version_note, description: e.target.value })
              }
              disabled={disabled}
              placeholder="Describe what changed in this version..."
              rows={2}
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StationForm;
